import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const NdaGate = () => {
  const [nda, setNda] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    api.get("/nda/me").then(({ data }) => setNda(data)).catch(() => {});
  }, []);

  const visible = !!nda && nda.required && (!nda.signed || justSigned);
  const showForm = visible && !justSigned;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !showForm) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#111827";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: p.clientX - r.left, y: p.clientY - r.top };
    };
    const down = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); e.preventDefault(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, [showForm]);

  if (!visible) return null;

  const clearInk = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const sign = async () => {
    setBusy(true);
    try {
      await api.post("/nda/sign", { typed_name: name.trim(), signature_image: canvasRef.current.toDataURL("image/png") });
      toast.success("Agreement signed — thank you!");
      setNda({ ...nda, signed: true });
      setJustSigned(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/nda/my-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "Teaching-NDA-signed.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (justSigned) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="nda-signed-panel">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
          <h2 className="text-lg font-bold font-display text-[#000000] mb-2">Agreement signed ✓</h2>
          <p className="text-sm text-[#000000] mb-4">A copy is stored on file. You can download your signed copy for your own records.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button className="btn-secondary text-sm" onClick={downloadPdf} data-testid="nda-download-pdf">Download signed copy (PDF)</button>
            <button className="btn-primary text-sm" onClick={() => setJustSigned(false)} data-testid="nda-continue-button">Continue to the app</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="nda-gate">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-5 sm:p-7 max-h-[92vh] flex flex-col">
        <h2 className="text-lg sm:text-xl font-bold font-display text-[#000000] mb-1">Teaching Confidentiality & Non-Disclosure Agreement</h2>
        <p className="text-xs text-[#000000] mb-3">Before you can continue, please read and sign this agreement. It covers what may and may not be taught or discussed outside your regular class time.</p>
        <div className="border border-[#E4E4E7] rounded-lg p-4 overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap text-[#000000] flex-1 min-h-[160px] max-h-[38vh] bg-[#FAFAFA]" data-testid="nda-text">
          {nda.text.split("\n").map((line, i) => {
            const t = line.trim();
            const isHead = t.startsWith("## ") || /^\d{1,2}\.\s+\S/.test(t);
            if (i === 0) return <div key={i} className="font-display font-bold text-sm mb-1">{t}</div>;
            if (isHead) return <div key={i} className="font-display font-bold mt-2">{t.replace(/^## /, "")}</div>;
            return <div key={i}>{line}</div>;
          })}
        </div>
        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-2 text-xs text-[#000000] cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="nda-agree-checkbox" />
            I have read and understood this agreement, and I agree to be bound by it.
          </label>
          <input className="input-base w-full text-sm" placeholder="Type your full legal name"
            value={name} onChange={(e) => setName(e.target.value)} data-testid="nda-name-input" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#000000] font-semibold">Draw your signature below (finger, stylus, or mouse) — required</span>
              {hasInk && (
                <button type="button" onClick={clearInk} className="text-[11px] px-2 py-0.5 rounded bg-[#0A0D14] text-white border-0 cursor-pointer" data-testid="nda-signature-clear">Clear</button>
              )}
            </div>
            <canvas ref={canvasRef} width={560} height={140}
              className="w-full h-[110px] rounded-lg bg-[#FAFAFA] border border-dashed border-[#A1A1AA] touch-none cursor-crosshair"
              data-testid="nda-signature-pad" />
          </div>
          <button className="btn-primary text-sm w-full" disabled={busy || !agree || name.trim().length < 3 || !hasInk}
            onClick={sign} data-testid="nda-sign-button">{busy ? "Signing…" : "Sign agreement"}</button>
        </div>
      </div>
    </div>
  );
};
